import {
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconBulb,
  IconPhotoOff,
  IconZoomIn,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../_base/component/_layout/_breadcrumb";
import { SectionCard, ZoomableImage } from "./HomeBlockShared";
import { designImage, HomeBlockTemplate, TEMPLATES } from "./homeBlockData";
import { GUIDE_BASICS, GUIDE_TEMPLATES, GuideSection, GuideShot, GuideTopic } from "./homeBlockGuideContent";
import classes from "./homeBlock.module.css";

export const GUIDE_PATH = "/fixed-content/home-block/huong-dan";

// Ảnh chụp 1.5x (public/guide/home-block): srcSet 1.5x để ảnh hiện đúng cỡ thật trên màn
const GUIDE_IMAGE_DIR = `${process.env.PUBLIC_URL}/guide/home-block`;

const isTemplate = (key: string): key is HomeBlockTemplate => key in GUIDE_TEMPLATES;

const ORDER: GuideTopic[] = [...GUIDE_BASICS, ...TEMPLATES.map((t) => GUIDE_TEMPLATES[t.key])];

const GuideFigure = ({ shot }: { shot: GuideShot }) => {
  const [opened, setOpened] = useState(false);
  const [missing, setMissing] = useState(false);
  const src = `${GUIDE_IMAGE_DIR}/${shot.file}.webp`;

  return (
    <Box component="figure" m={0}>
      {missing ? (
        <Group gap={8} justify="center" py={32} className={classes.guideShotMissing}>
          <IconPhotoOff size={18} aria-hidden />
          <Text fz={13}>Chưa có ảnh minh hoạ cho phần này.</Text>
        </Group>
      ) : (
        <button
          type="button"
          className={`${classes.zoomButton} ${classes.guideShotButton}`}
          aria-label={`Phóng to ảnh: ${shot.caption}`}
          title="Bấm để phóng to"
          onClick={() => setOpened(true)}
        >
          <img
            src={src}
            srcSet={`${src} 1.5x`}
            alt={shot.caption}
            loading="lazy"
            className={classes.guideShot}
            onError={() => setMissing(true)}
          />
          <span className={classes.zoomHint} aria-hidden="true">
            <IconZoomIn size={16} />
          </span>
        </button>
      )}
      <Text component="figcaption" fz={12} mt={6} className={classes.muted}>
        {shot.caption}
        {missing ? "" : ". Bấm ảnh để phóng to."}
      </Text>
      {shot.marks.length > 0 && (
        <ol className={classes.markList} aria-label={`Chú thích ảnh ${shot.caption}`}>
          {shot.marks.map((mark, i) => (
            <li key={mark}>
              <span className={classes.markNum} aria-hidden>
                {i + 1}
              </span>
              <Text component="span" fz={14} lh={1.5}>
                {mark}
              </Text>
            </li>
          ))}
        </ol>
      )}
      <Modal opened={opened} onClose={() => setOpened(false)} title={shot.caption} size="auto" centered>
        <img src={src} alt={shot.caption} style={{ display: "block", maxWidth: "100%", height: "auto" }} />
      </Modal>
    </Box>
  );
};

const GuideSectionCard = ({ section, id }: { section: GuideSection; id: string }) => (
  <SectionCard id={id} title={section.title}>
    {section.intro && (
      <Text fz={14} lh={1.55} c="var(--hb-ink-2)" maw="75ch">
        {section.intro}
      </Text>
    )}
    {section.steps && (
      <ol className={classes.stepList}>
        {section.steps.map((step) => (
          <li key={step}>
            <Text component="span" fz={14} lh={1.55}>
              {step}
            </Text>
          </li>
        ))}
      </ol>
    )}
    {section.shot && <GuideFigure shot={section.shot} />}
    {section.tips && (
      <Box className={classes.guideTip}>
        <Group gap={6} mb={6} wrap="nowrap">
          <IconBulb size={16} aria-hidden />
          <Text fz={13} fw={700}>
            Lưu ý
          </Text>
        </Group>
        <Stack component="ul" gap={4} m={0} pl={20}>
          {section.tips.map((tip) => (
            <Text component="li" key={tip} fz={13} lh={1.5}>
              {tip}
            </Text>
          ))}
        </Stack>
      </Box>
    )}
  </SectionCard>
);

const TopicHeader = ({ topic }: { topic: GuideTopic }) => {
  const template = isTemplate(topic.key) ? TEMPLATES.find((t) => t.key === topic.key) : undefined;
  return (
    <Paper withBorder radius="md" p={20}>
      <Flex gap={20} wrap="wrap" align="flex-start">
        {template && (
          <Box w={260} maw="100%">
            <ZoomableImage imageId={template.thumbId} alt={`Ảnh thiết kế mẫu ${template.name}`} />
          </Box>
        )}
        <Stack gap={8} miw={0} style={{ flex: "1 1 320px" }}>
          <Text fz={12} fw={600} className={classes.muted}>
            {template ? "Hướng dẫn theo mẫu block" : "Hướng dẫn chung"}
          </Text>
          <Title order={2} fz={22} lh={1.25}>
            {template ? `Mẫu ${template.name}` : topic.title}
          </Title>
          <Text fz={14} lh={1.55} c="var(--hb-ink-2)" maw="75ch">
            {topic.summary}
          </Text>
          {template && (
            <Group gap={6}>
              <Text fz={13} className={classes.muted}>
                Đang dùng cho:
              </Text>
              <Badge variant="light" color="gray" radius="xl" tt="none" fw={600}>
                {template.used}
              </Badge>
            </Group>
          )}
          {topic.sections.length > 1 && (
            <Group gap={4} mt={4} component="nav" aria-label="Các phần trong mục này">
              {topic.sections.map((s, i) => (
                <button
                  key={s.title}
                  type="button"
                  className={classes.guideJump}
                  onClick={() =>
                    document
                      .getElementById(`hb-guide-${i}`)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  {s.title}
                </button>
              ))}
            </Group>
          )}
        </Stack>
      </Flex>
    </Paper>
  );
};

const HomeBlockGuide = () => {
  const { topic: topicKey } = useParams();
  const navigate = useNavigate();
  const index = Math.max(
    0,
    ORDER.findIndex((t) => t.key === topicKey)
  );
  const topic = ORDER[index];
  const prev = ORDER[index - 1];
  const next = ORDER[index + 1];

  // Đổi mục thì về đầu trang, không giữ vị trí cuộn của mục trước
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [topic.key]);

  const navLink = (t: GuideTopic, label: React.ReactNode) => (
    <Link
      key={t.key}
      to={`${GUIDE_PATH}/${t.key}`}
      className={`${classes.toc} ${t.key === topic.key ? classes.tocActive : ""}`}
      aria-current={t.key === topic.key ? "page" : undefined}
    >
      {label}
    </Link>
  );

  return (
    <Box className={classes.page}>
      <BreadCrumb />
      <Stack gap={16} mt={8}>
        <Stack gap={6} miw={0}>
          <Anchor
            component={Link}
            to="/fixed-content/home-block"
            fz={13}
            fw={600}
            underline="never"
            c="var(--hb-accent)"
            w="fit-content"
          >
            <Group gap={6}>
              <IconArrowLeft size={16} />
              Danh sách block
            </Group>
          </Anchor>
          <Title order={1} fz={24} lh={1.2}>
            Hướng dẫn sử dụng Block trang chủ
          </Title>
        </Stack>

        <Select
          hiddenFrom="md"
          aria-label="Chọn mục hướng dẫn"
          value={topic.key}
          onChange={(value) => value && navigate(`${GUIDE_PATH}/${value}`)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          data={[
            {
              group: "Hướng dẫn chung",
              items: GUIDE_BASICS.map((t) => ({ value: t.key, label: t.title })),
            },
            {
              group: "Theo mẫu block",
              items: TEMPLATES.map((t) => ({ value: t.key, label: `Mẫu ${t.name}` })),
            },
          ]}
        />

        <Flex align="flex-start" gap={16}>
          <Box
            visibleFrom="md"
            className={classes.detailSticky}
            style={{ flex: "0 0 280px" }}
          >
            <Paper withBorder radius="md" p={8} component="nav" aria-label="Mục lục hướng dẫn">
              <Text fz={12} fw={600} px={10} pt={6} pb={4} className={classes.muted}>
                Hướng dẫn chung
              </Text>
              {GUIDE_BASICS.map((t) => navLink(t, t.title))}
              <Text fz={12} fw={600} px={10} pt={14} pb={4} className={classes.muted}>
                Theo mẫu block
              </Text>
              {TEMPLATES.map((t) =>
                navLink(
                  GUIDE_TEMPLATES[t.key],
                  <Group gap={10} wrap="nowrap">
                    <img
                      src={designImage(t.thumbId, 240)}
                      alt=""
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className={classes.guideNavThumb}
                    />
                    <Stack gap={0} miw={0}>
                      <Text component="span" fz={14} fw={500} lh={1.3} truncate>
                        {t.name}
                      </Text>
                      <Text component="span" fz={12} lh={1.3} truncate className={classes.muted}>
                        {t.used}
                      </Text>
                    </Stack>
                  </Group>
                )
              )}
            </Paper>
          </Box>

          <Stack gap={16} miw={0} style={{ flex: "1 1 auto" }}>
            <TopicHeader topic={topic} />
            {topic.sections.map((section, i) => (
              <GuideSectionCard key={section.title} section={section} id={`hb-guide-${i}`} />
            ))}
            <Flex justify="space-between" gap={12} wrap="wrap">
              {prev ? (
                <Button
                  component={Link}
                  to={`${GUIDE_PATH}/${prev.key}`}
                  variant="default"
                  leftSection={<IconArrowLeft size={16} />}
                >
                  {isTemplate(prev.key) ? `Mẫu ${prev.title}` : prev.title}
                </Button>
              ) : (
                <span />
              )}
              {next && (
                <Button
                  component={Link}
                  to={`${GUIDE_PATH}/${next.key}`}
                  variant="default"
                  rightSection={<IconArrowRight size={16} />}
                >
                  {isTemplate(next.key) ? `Mẫu ${next.title}` : next.title}
                </Button>
              )}
            </Flex>
          </Stack>
        </Flex>
      </Stack>
    </Box>
  );
};

export default HomeBlockGuide;
